/**
 * The account this browser last signed in with — so a returning person sees "Continue as ana@…" and one button,
 * instead of being asked again. Per device, never sent anywhere: it only spares typing, and every sign-in still
 * proves who the person is (passkey, SSO or a fresh code).
 *
 * Written only after a sign-in succeeds. SSO is the exception the page cannot see the end of (the IdP redirects
 * back into the app), so it is written as `pending` before the redirect and confirmed by the first `/api/me` that
 * comes back signed in (`confirmRemembered`); a failed SSO return drops it.
 *
 * Storage can be missing or throw (private windows, blocked site data): every access is guarded, and the page works
 * without it. A shared computer: "Remove from this device" forgets it.
 */

export type SignInMethod = 'passkey' | 'code' | 'sso'

export interface RememberedAccount {
  email: string
  method: SignInMethod
  /** The company SSO, when `method` is `sso`. */
  sso?: { slug: string; label: string }
  /** SSO only: written before the redirect, not yet confirmed by a signed-in `/api/me`. */
  pending?: boolean
}

/** The subset of `Storage` used here, so tests can pass a plain object. */
export interface KeyValueStore {
  getItem(key: string): string | null
  setItem(key: string, value: string): void
  removeItem(key: string): void
}

const ACCOUNT_KEY = 'smith.signin.account.v1'
const DISMISSED_KEY = 'smith.signin.passkeyOfferDismissed.v1'
/** How long "Not now" to the passkey offer keeps it away, for that address on this device. */
export const PASSKEY_OFFER_SNOOZE_MS = 30 * 24 * 60 * 60 * 1000

function browserStore(): KeyValueStore | null {
  try {
    return typeof window !== 'undefined' && window.localStorage ? window.localStorage : null
  } catch {
    return null
  }
}

function read(store: KeyValueStore | null, key: string): unknown {
  if (!store) return null
  try {
    const raw = store.getItem(key)
    return raw ? (JSON.parse(raw) as unknown) : null
  } catch {
    return null
  }
}

function write(store: KeyValueStore | null, key: string, value: unknown): void {
  if (!store) return
  try {
    store.setItem(key, JSON.stringify(value))
  } catch {
    // Storage full or blocked: the next sign-in simply asks for the address again.
  }
}

function isAccount(v: unknown): v is RememberedAccount {
  if (!v || typeof v !== 'object') return false
  const a = v as Partial<RememberedAccount>
  if (typeof a.email !== 'string' || !a.email.includes('@')) return false
  if (a.method !== 'passkey' && a.method !== 'code' && a.method !== 'sso') return false
  if (a.method === 'sso' && (typeof a.sso?.slug !== 'string' || typeof a.sso?.label !== 'string')) return false
  return true
}

export function readRemembered(store: KeyValueStore | null = browserStore()): RememberedAccount | null {
  const v = read(store, ACCOUNT_KEY)
  return isAccount(v) ? v : null
}

export function remember(account: RememberedAccount, store: KeyValueStore | null = browserStore()): void {
  write(store, ACCOUNT_KEY, { ...account, email: account.email.trim().toLowerCase() })
}

export function forgetRemembered(store: KeyValueStore | null = browserStore()): void {
  if (!store) return
  try {
    store.removeItem(ACCOUNT_KEY)
  } catch {
    // nothing to do
  }
}

/**
 * A signed-in `/api/me` confirms a pending SSO account — under the address the IdP actually signed in, which can
 * differ from the one typed (an alias). A non-pending account the session disagrees with is left alone: the person
 * may simply be signed in elsewhere in another tab.
 */
export function confirmRemembered(signedInEmail: string, store: KeyValueStore | null = browserStore()): void {
  const current = readRemembered(store)
  if (!current?.pending) return
  const { pending: _pending, ...confirmed } = current
  remember({ ...confirmed, email: signedInEmail }, store)
}

export function passkeyOfferSnoozed(
  email: string,
  now = Date.now(),
  store: KeyValueStore | null = browserStore()
): boolean {
  const map = read(store, DISMISSED_KEY)
  if (!map || typeof map !== 'object') return false
  const at = (map as Record<string, unknown>)[email.trim().toLowerCase()]
  return typeof at === 'number' && now - at < PASSKEY_OFFER_SNOOZE_MS
}

export function snoozePasskeyOffer(
  email: string,
  now = Date.now(),
  store: KeyValueStore | null = browserStore()
): void {
  const map = read(store, DISMISSED_KEY)
  const next = { ...(map && typeof map === 'object' ? (map as Record<string, number>) : {}) }
  next[email.trim().toLowerCase()] = now
  write(store, DISMISSED_KEY, next)
}
